import { motion, AnimatePresence } from "framer-motion";
import { Copy, Check, Share2, ChevronDown } from "lucide-react";
import { PLATFORM_OPTIONS } from "@/hooks/use-studio";

interface StudioCopyShareBarProps {
  copied: boolean;
  refining: string | null;
  handleCopy: () => Promise<void>;
  shared: boolean;
  handleShare: () => void;
  platformCopyOpen: boolean;
  setPlatformCopyOpen: (v: boolean) => void;
  smartCopyLabel: string | null;
  copiedPlatform: string | null;
  handleCopyForPlatform: (id: string, fmt: (s: string) => string) => Promise<void>;
}

export function StudioCopyShareBar({
  copied,
  refining,
  handleCopy,
  shared,
  handleShare,
  platformCopyOpen,
  setPlatformCopyOpen,
  smartCopyLabel,
  copiedPlatform,
  handleCopyForPlatform,
}: StudioCopyShareBarProps) {
  return (
    <div className="px-4 pb-4">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-1">
        {/* Primary: Smart Copy */}
        <div className="relative flex-1">
          <motion.button
            data-testid="copy-btn"
            type="button"
            onClick={() => handleCopy()}
            disabled={!!refining}
            animate={copied ? { scale: [1, 1.06, 1], backgroundColor: ["hsl(var(--primary))", "hsl(142 76% 36%)", "hsl(142 76% 36%)"] } : { scale: 1 }}
            transition={{ duration: 0.35, type: "spring", stiffness: 400, damping: 18 }}
            className="w-full flex items-center justify-center gap-2 h-11 px-5 rounded-xl text-sm font-semibold text-primary-foreground disabled:opacity-50 select-none"
            style={{ background: copied ? "hsl(142 76% 36%)" : "hsl(var(--primary))" }}
            title="Copy to clipboard (Cmd/Ctrl+C)"
          >
            <AnimatePresence mode="wait" initial={false}>
              {copied ? (
                <motion.span
                  key="check"
                  initial={{ scale: 0.4, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.4, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 22 }}
                  className="flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Copied!
                </motion.span>
              ) : (
                <motion.span
                  key="copy"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
          <AnimatePresence>
            {smartCopyLabel && copied && (
              <motion.p
                key="smart-label"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.18 }}
                className="absolute left-0 right-0 text-center text-[10px] text-green-500 font-medium mt-1 pointer-events-none"
                style={{ top: "100%" }}
              >
                {smartCopyLabel}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        {/* Secondary: Share */}
        <button
          type="button"
          onClick={handleShare}
          disabled={!!refining}
          title={typeof navigator.share === "function" ? "Share" : "Share / Copy link"}
          className="flex items-center justify-center gap-2 h-11 px-5 rounded-xl text-sm font-semibold border border-border/60 text-foreground bg-secondary/30 hover:bg-secondary/60 transition-colors disabled:opacity-50 sm:w-auto w-full"
        >
          {shared ? <Check className="w-4 h-4 text-green-500" /> : <Share2 className="w-4 h-4" />}
          {shared ? "Shared!" : "Share"}
        </button>

        {/* Other formats dropdown */}
        <div
          className="relative"
          onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setPlatformCopyOpen(false); }}
        >
          <button
            type="button"
            data-testid="platform-copy-btn"
            onClick={() => setPlatformCopyOpen(!platformCopyOpen)}
            disabled={!!refining}
            title="Copy for a specific platform"
            className="flex items-center justify-center gap-1.5 h-11 px-3 rounded-xl text-xs font-medium border border-border/40 text-muted-foreground hover:text-foreground hover:border-border/70 transition-colors disabled:opacity-50 w-full sm:w-auto whitespace-nowrap"
          >
            Other formats
            <ChevronDown className={`w-3 h-3 transition-transform duration-150 ${platformCopyOpen ? "rotate-180" : ""}`} />
          </button>
          <AnimatePresence>
            {platformCopyOpen && (
              <motion.div
                key="platform-menu"
                initial={{ opacity: 0, scale: 0.95, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -4 }}
                transition={{ duration: 0.12 }}
                className="absolute right-0 bottom-full mb-1 z-20 bg-card border border-border/60 rounded-xl shadow-lg overflow-hidden min-w-[200px]"
              >
                <div className="px-3 py-1.5 border-b border-border/30">
                  <p className="text-[10px] font-bold text-muted-foreground/50 uppercase tracking-widest">Copy for...</p>
                </div>
                {PLATFORM_OPTIONS.map(({ id, label, fmt, desc }) => (
                  <button
                    key={id}
                    data-testid={`platform-copy-${id}`}
                    type="button"
                    onClick={() => handleCopyForPlatform(id, fmt)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-secondary/40 transition-colors"
                  >
                    <div>
                      <p className="text-xs font-medium text-foreground">{label}</p>
                      <p className="text-[10px] text-muted-foreground/60">{desc}</p>
                    </div>
                    {copiedPlatform === id
                      ? <span className="flex items-center gap-1 text-[10px] font-medium text-green-500 shrink-0"><Check className="w-3 h-3" />Copied!</span>
                      : <Copy className="w-3 h-3 text-muted-foreground/40 shrink-0" />
                    }
                  </button>
                ))}
                <div className="border-t border-border/30">
                  <button
                    type="button"
                    data-testid="platform-copy-plain"
                    onClick={handleCopy}
                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-secondary/40 transition-colors"
                  >
                    <div>
                      <p className="text-xs font-medium text-foreground">Plain text</p>
                      <p className="text-[10px] text-muted-foreground/60">No formatting changes</p>
                    </div>
                    {copied
                      ? <span className="flex items-center gap-1 text-[10px] font-medium text-green-500 shrink-0"><Check className="w-3 h-3" />Copied!</span>
                      : <Copy className="w-3 h-3 text-muted-foreground/40 shrink-0" />
                    }
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      {smartCopyLabel && copied && <div className="h-5" />}
    </div>
  );
}
